/**
 * Interaction Service
 * API client for interactions and conversions
 */

import { apiClient, type ApiResponse } from './api-client';

export interface ConvertToCaseData {
  title?: string;
  description?: string;
  area?: string;
  priority?: string;
  practiceArea?: string;
  caseType?: string;
}

export interface ConvertToLegalProcessData {
  courtNumber: string; // Required
  title?: string;
  description?: string;
  courtName?: string;
  judgeName?: string;
  area?: string;
  priority?: string;
  practiceArea?: string;
  caseType?: string;
  estimatedValue?: number;
  tags?: string[];
}

const CASE_AREA_ALIASES: Record<string, string> = {
  civil: 'CIVIL',
  civel: 'CIVIL',
  cível: 'CIVIL',
  criminal: 'CRIMINAL',
  penal: 'CRIMINAL',
  trabalhista: 'LABOR',
  trabalho: 'LABOR',
  labor: 'LABOR',
  familia: 'FAMILY',
  família: 'FAMILY',
  empresarial: 'CORPORATE',
  corporativo: 'CORPORATE',
  tributario: 'TAX',
  tributário: 'TAX',
  fiscal: 'TAX',
  administrativo: 'ADMINISTRATIVE'
};

function normalizeCaseArea(area?: string): string | undefined {
  if (!area?.trim()) return undefined;
  const trimmed = area.trim();
  const upper = trimmed.toUpperCase();
  if (['CIVIL', 'CRIMINAL', 'LABOR', 'FAMILY', 'CORPORATE', 'TAX', 'ADMINISTRATIVE'].includes(upper)) {
    return upper;
  }
  return CASE_AREA_ALIASES[trimmed.toLowerCase()];
}

export const interactionService = {
  /**
   * Convert an interaction to a case
   */
  async convertToCase(
    interactionId: string,
    data?: ConvertToCaseData
  ): Promise<ApiResponse<Record<string, unknown>>> {
    const payload = data ? {
      title: data.title,
      description: data.description,
      area: normalizeCaseArea(data.area || data.practiceArea),
      priority: data.priority
    } : {};

    const response = await apiClient.post(
      `/api/interactions/${interactionId}/convert-to-case`,
      payload
    );
    return response.data;
  },

  /**
   * Convert an interaction directly to a legal process
   */
  async convertToLegalProcess(
    interactionId: string,
    data: ConvertToLegalProcessData
  ): Promise<ApiResponse<Record<string, unknown>>> {
    const payload = {
      title: data.title,
      description: data.description,
      courtNumber: data.courtNumber,
      courtName: data.courtName,
      judgeName: data.judgeName,
      area: normalizeCaseArea(data.area || data.practiceArea),
      priority: data.priority,
      estimatedValue: data.estimatedValue,
      tags: data.tags
    };

    const response = await apiClient.post(
      `/api/interactions/${interactionId}/convert-to-legal-process`,
      payload
    );
    return response.data;
  }
};
